import type { FormEvent, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StoreSelect, type StoreSelectOption } from '@/pages/store/components/StoreSelect';
import { formatNumber } from '@/lib/format-es';
import { GEO_PRESETS, parseCenter } from '../captureExportModel';
import { codeLabel } from '../captureLabels';
import type { CaptureExportFacets, CaptureExportFilters, CaptureFacetOption, CaptureVisibility } from '../types';

const PLATFORM_OPTIONS: readonly StoreSelectOption[] = [
    { value: '', label: 'Cualquiera' },
    { value: 'costa', label: 'Costa' },
    { value: 'kayak', label: 'Kayak' },
    { value: 'embarcacion', label: 'Embarcación' },
    { value: 'unknown', label: 'Sin plataforma' },
];

const VISIBILITY_OPTIONS: readonly StoreSelectOption[] = [
    { value: 'all', label: 'Públicas y privadas' },
    { value: 'public', label: 'Solo públicas' },
    { value: 'private', label: 'Solo privadas' },
];

const GEO_OPTIONS: readonly StoreSelectOption[] = [
    { value: '', label: 'Elegir ciudad' },
    ...GEO_PRESETS.map((preset) => ({ value: preset.center, label: preset.label })),
];

function facetOptions(options: readonly CaptureFacetOption[], anyLabel: string): StoreSelectOption[] {
    return [
        { value: '', label: anyLabel },
        ...options.map((option) => ({
            value: option.value,
            label: `${codeLabel(option.value)} (${formatNumber(option.count)})`,
        })),
    ];
}

function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id} className="text-xs text-muted-foreground">
                {label}
            </Label>
            {children}
        </div>
    );
}

function RangeField({
    id,
    label,
    min,
    max,
    onMin,
    onMax,
}: {
    id: string;
    label: string;
    min: string;
    max: string;
    onMin: (value: string) => void;
    onMax: (value: string) => void;
}) {
    return (
        <Field id={`${id}-min`} label={label}>
            <div className="flex items-center gap-2">
                <Input id={`${id}-min`} inputMode="decimal" placeholder="Mín." value={min} onChange={(e) => onMin(e.target.value)} />
                <span className="text-muted-foreground">–</span>
                <Input aria-label={`${label} máximo`} inputMode="decimal" placeholder="Máx." value={max} onChange={(e) => onMax(e.target.value)} />
            </div>
        </Field>
    );
}

export function CaptureFiltersPanel({
    draft,
    facets,
    onChange,
    onApply,
    onReset,
}: {
    draft: CaptureExportFilters;
    facets: CaptureExportFacets | undefined;
    onChange: (patch: Partial<CaptureExportFilters>) => void;
    onApply: () => void;
    onReset: () => void;
}) {
    const centerInvalid = draft.center.trim() !== '' && parseCenter(draft.center) == null;
    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        onApply();
    };

    return (
        <form onSubmit={handleSubmit} className="rounded-xl border border-border bg-card p-4 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Field id="capture-species" label="Especie">
                    <Input
                        id="capture-species"
                        placeholder="Lubina, Dicentrarchus…"
                        value={draft.species}
                        onChange={(e) => onChange({ species: e.target.value })}
                    />
                </Field>
                <Field id="capture-bait" label="Señuelo o cebo">
                    <Input
                        id="capture-bait"
                        placeholder="Vinilo, minnow, navaja…"
                        value={draft.bait}
                        onChange={(e) => onChange({ bait: e.target.value })}
                    />
                </Field>
                <Field id="capture-bait-type" label="Tipo de cebo">
                    <StoreSelect
                        id="capture-bait-type"
                        ariaLabel="Tipo de cebo"
                        value={draft.baitType}
                        options={facetOptions(facets?.baitTypes ?? [], 'Cualquiera')}
                        onChange={(value) => onChange({ baitType: value })}
                    />
                </Field>
                <Field id="capture-technique" label="Técnica">
                    <StoreSelect
                        id="capture-technique"
                        ariaLabel="Técnica"
                        value={draft.technique}
                        options={facetOptions(facets?.techniques ?? [], 'Cualquiera')}
                        onChange={(value) => onChange({ technique: value })}
                    />
                </Field>

                <RangeField
                    id="capture-weight"
                    label="Peso (kg)"
                    min={draft.minWeightKg}
                    max={draft.maxWeightKg}
                    onMin={(value) => onChange({ minWeightKg: value })}
                    onMax={(value) => onChange({ maxWeightKg: value })}
                />
                <RangeField
                    id="capture-length"
                    label="Talla (cm)"
                    min={draft.minLengthCm}
                    max={draft.maxLengthCm}
                    onMin={(value) => onChange({ minLengthCm: value })}
                    onMax={(value) => onChange({ maxLengthCm: value })}
                />
                <Field id="capture-platform" label="Plataforma">
                    <StoreSelect
                        id="capture-platform"
                        ariaLabel="Plataforma"
                        value={draft.platform}
                        options={PLATFORM_OPTIONS}
                        onChange={(value) => onChange({ platform: value })}
                    />
                </Field>
                <Field id="capture-water" label="Tipo de agua">
                    <StoreSelect
                        id="capture-water"
                        ariaLabel="Tipo de agua"
                        value={draft.waterType}
                        options={facetOptions(facets?.waterTypes ?? [], 'Cualquiera')}
                        onChange={(value) => onChange({ waterType: value })}
                    />
                </Field>
                <Field id="capture-visibility" label="Visibilidad">
                    <StoreSelect
                        id="capture-visibility"
                        ariaLabel="Visibilidad"
                        value={draft.visibility}
                        options={VISIBILITY_OPTIONS}
                        onChange={(value) => onChange({ visibility: value as CaptureVisibility })}
                    />
                </Field>

                <Field id="capture-center" label="Zona: centro (lat, lng)">
                    <div className="flex gap-2">
                        <Input
                            id="capture-center"
                            placeholder="39.4699, -0.3763"
                            value={draft.center}
                            aria-invalid={centerInvalid}
                            className={centerInvalid ? 'border-destructive' : undefined}
                            onChange={(e) => onChange({ center: e.target.value })}
                        />
                        <div className="w-36 shrink-0">
                            <StoreSelect
                                ariaLabel="Ciudad de referencia"
                                value=""
                                options={GEO_OPTIONS}
                                placeholder="Ciudad"
                                onChange={(value) =>
                                    onChange(value ? { center: value, radiusKm: draft.radiusKm || '50' } : { center: '' })
                                }
                            />
                        </div>
                    </div>
                    {centerInvalid && (
                        <p className="text-xs text-destructive">Pega las coordenadas como «39.47, -0.38».</p>
                    )}
                </Field>
                <Field id="capture-radius" label="Radio (km)">
                    <Input
                        id="capture-radius"
                        inputMode="decimal"
                        placeholder="50"
                        value={draft.radiusKm}
                        onChange={(e) => onChange({ radiusKm: e.target.value })}
                    />
                </Field>
                <Field id="capture-date-from" label="Desde">
                    <Input id="capture-date-from" type="date" value={draft.dateFrom} onChange={(e) => onChange({ dateFrom: e.target.value })} />
                </Field>
                <Field id="capture-date-to" label="Hasta">
                    <Input id="capture-date-to" type="date" value={draft.dateTo} onChange={(e) => onChange({ dateTo: e.target.value })} />
                </Field>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground">
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            className="accent-primary"
                            checked={draft.withPhotosOnly}
                            onChange={(e) => onChange({ withPhotosOnly: e.target.checked })}
                        />
                        Solo con foto
                    </label>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            className="accent-primary"
                            checked={draft.reliableSpeciesOnly}
                            onChange={(e) => onChange({ reliableSpeciesOnly: e.target.checked })}
                        />
                        Excluir especie dudosa
                    </label>
                    <label className="flex items-center gap-2">
                        Mín. likes
                        <Input
                            aria-label="Mínimo de likes"
                            inputMode="numeric"
                            className="h-8 w-20"
                            value={draft.minLikes}
                            onChange={(e) => onChange({ minLikes: e.target.value })}
                        />
                    </label>
                </div>
                <div className="flex gap-2">
                    <Button type="button" variant="ghost" size="sm" onClick={onReset}>
                        Limpiar
                    </Button>
                    <Button type="submit" size="sm" disabled={centerInvalid}>
                        Aplicar filtros
                    </Button>
                </div>
            </div>
        </form>
    );
}
